import {
    Box,
    Button,
    Card,
    Container,
    FormControl,
    Autocomplete,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    InputLabel
} from "@mui/material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import {useEffect, useRef, useState} from "react";
import { Layout } from "../layout/Layout";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {getOrdersByDate, getOrdersByDateAndClient} from "../../api/orderService";
import {Order} from "../../types/order";
import {useHeaders} from "../../hooks/useHeaders";
import {clientGetAll} from "../../api/clientService";
import {setClients} from "../../features/clients/clientSlice";
import {Client} from "../../types/client";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {resetOrders, setOrders} from "../../features/orders/orderSlice";
import {useGlobalData} from "../../hooks/useGlobalData";
import {ReportsTable} from "./ReportsTable";
import * as XLSX from 'xlsx';
import {Auth} from "../../types/auth";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {OrderReceiptPdf2} from "../orders/orderReceiptPdf/OrderReceiptPdf2";
import {OrderInvoicesPdf} from "../orders/orderReceiptPdf/OrderInvoicesPdf";
import {orderDelete, orderUpdate} from "../../api/orderService";

interface TransformedOrder {
    "Numri i porosis": string;
    "Data e porosis": string;
    Klienti: string;
    "Numri i leternjoftimit": string;
    Lloji: string;
    "Sasia (kg)": number | string;
    Çmimi: string;
    Totali: string;
}

interface FilterData {
    client: Client;
    clients: Client[];
    orders: TransformedOrder[];
    startDate: dayjs.Dayjs;
    endDate: dayjs.Dayjs;
    isFilteredByDate: boolean;
    isFilteredByClient: boolean;
}

export const Reports = () => {
    const headers = useHeaders();
    const dispatch = useDispatch();
    const clients = useSelector((state: RootState) => state.clients);
    const orders = useSelector((state: RootState) => state.orders);
    const [filterData, setFilterData] = useState<FilterData>({
        client: {},
        clients: [],
        orders: [],
        startDate: dayjs(new Date()),
        endDate: dayjs(new Date()),
        isFilteredByDate: false,
        isFilteredByClient: false

    });
    const globalData = useGlobalData();
    const defaultProps = {
        options: filterData.clients,
        getOptionLabel: (option: Client) => `${option.firstName} ${option.lastName}`,
    };
    const [totalPrice, setTotalPrice] = useState<number | string>(0);
    const [totalKilograms, setTotalKilograms] = useState(0);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);
    const invoicesRef = useRef<HTMLDivElement>(null);
    const [isGeneratingInvoicesPDF, setIsGeneratingInvoicesPDF] = useState(false);
    const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [selectedAction, setSelectedAction] = useState<
        null | 'delete_selected' | 'delete_all' | 'update_orderNumber_selected' | 'update_orderNumber_all'
    >(null);
    const [isEditingOrderNumbers, setIsEditingOrderNumbers] = useState(false);
    const [orderNumberDrafts, setOrderNumberDrafts] = useState<Record<number, string>>({});
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
    const [pendingDeleteMode, setPendingDeleteMode] = useState<null | 'selected' | 'allVisible'>(null);

    useEffect(() => {
        const getClients = async () => {
            dispatch(resetOrders());
            const clientsResponse: any = await clientGetAll(headers);

            if(clientsResponse)
                dispatch(setClients(clientsResponse as Client[]));
        }

        getClients();
    }, [dispatch]);

    useEffect(() => {
        setSelectedOrderIds([]);
        setIsEditingOrderNumbers(false);
        setOrderNumberDrafts({});
    }, [orders]);

    const onDateChange = (key: any, val: any) => {
        setFilterData({
            ...filterData,
            [key] : val
        });
        console.log(clients)
    };

    const refreshOrders = async (maybeClient?: Client | null) => {
        const hasClient = Boolean(maybeClient?.idClient);
        const dataBase = {
            startDate: filterData.startDate?.format('YYYY-MM-DD'),
            endDate: filterData.endDate?.format('YYYY-MM-DD'),
            year: filterData.startDate?.year()
        };

        if (hasClient) {
            const data = { ...dataBase, idClient: Number(maybeClient?.idClient) };
            const orders = await getOrdersByDateAndClient(data, headers);
            if (orders) dispatch(setOrders(orders as Order[]));
            setFilterData((prev) => ({ ...prev, isFilteredByClient: true, client: maybeClient ?? {}, isFilteredByDate: true }));
            return;
        }

        const orders = await getOrdersByDate(dataBase, headers);
        if (orders) dispatch(setOrders(orders as Order[]));
        setFilterData((prev) => ({ ...prev, isFilteredByDate: true, isFilteredByClient: false, client: {} }));
    }

    const filterByDate = async () => {
        try{
            const data = {
                startDate: filterData.startDate?.format('YYYY-MM-DD'),
                endDate: filterData.endDate?.format('YYYY-MM-DD'),
                year: filterData.startDate?.year()
            }

            let transformedOrders: TransformedOrder[] = [];
            const orders = await getOrdersByDate(data, headers);

            if(orders){
                dispatch(setOrders(orders as Order[]));
                transformedOrders = orders.map((order: any) => ({
                    "Numri i porosis": String(order.orderNumber ?? ''),
                    "Data e porosis": dayjs(order.orderDate).format('DD.MM.YYYY'),
                    Klienti: `${order.idClient.firstName} ${order.idClient.lastName}`,
                    "Numri i leternjoftimit": order.idClient.nationalId,
                    Lloji: order.type,
                    "Sasia (kg)": order.quantity_per_kg,
                    Çmimi: order.price,
                    Totali: order.totalPrice
                }));

                const totalKg = transformedOrders.reduce((acc, order) => acc + Number(order["Sasia (kg)"]), 0);
                const totalPrc  = transformedOrders.reduce((acc, order) => acc + parseFloat(order.Totali), 0);

                setTotalKilograms(totalKg);
                setTotalPrice(totalPrc.toFixed(2));

                transformedOrders.push({
                    "Numri i porosis": '',
                    "Data e porosis": '',
                    Klienti: '',
                    "Numri i leternjoftimit": '',
                    Lloji: '',
                    "Sasia (kg)": 'Kg: ' + totalKg,
                    Çmimi: '',
                    Totali: 'Totali: ' + totalPrc.toFixed(2)
                });

                setFilterData({...filterData, })
            }

            const clientIds = new Set(orders.map((order) => order?.idClient?.idClient));
            const filteredClients = clients.filter(client => clientIds.has(client.idClient));

            setFilterData({
                ...filterData,
                clients: filteredClients,
                isFilteredByDate: true,
                orders: transformedOrders
            });
        }catch (error){
            console.error('Failed getting orders', error);
        }
    }

    const filterByDateAndClient = async (value: any) => {
        try{
            console.log(value)
            setFilterData((prev) => ({
                ...prev,
                client: value ?? {},
                isFilteredByClient: Boolean(value?.idClient)
            }));
            const data = {
                idClient: Number(value?.idClient ?? ''),
                startDate: filterData.startDate?.format('YYYY-MM-DD'),
                endDate: filterData.endDate?.format('YYYY-MM-DD'),
                year: filterData.startDate?.year()
            }

            const orders = await getOrdersByDateAndClient(data, headers);

            if(orders){
                dispatch(setOrders(orders as Order[]));
                setFilterData({...filterData, isFilteredByClient: true});
                const totalKg = orders.reduce((acc, order) => acc + Number(order.quantity_per_kg), 0);
                const totalPrc = orders.reduce((acc, order) => acc + Number(order.totalPrice ?? 0), 0);

                setTotalKilograms(totalKg);
                setTotalPrice(totalPrc.toFixed(2));
            }
        }catch (error){
            console.error('Failed getting orders', error);
        }
    }

    const onToggleOrderId = (idOrder: number) => {
        setSelectedOrderIds((prev) => prev.includes(idOrder) ? prev.filter((id) => id !== idOrder) : [...prev, idOrder]);
    }

    const onToggleAllVisible = () => {
        const visibleIds = orders
            .map((o) => o.idOrder)
            .filter((id): id is number => typeof id === 'number');

        const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedOrderIds.includes(id));

        setSelectedOrderIds((prev) => {
            if (allSelected) return prev.filter((id) => !visibleIds.includes(id));
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return Array.from(next);
        });
    }

    const clearSelectionAndAction = () => {
        setSelectedOrderIds([]);
        setSelectedAction(null);
        setIsEditingOrderNumbers(false);
        setOrderNumberDrafts({});
    }

    const bulkDelete = async (mode: 'selected' | 'allVisible') => {
        const ids =
            mode === 'selected'
                ? selectedOrderIds
                : orders.map((o) => o.idOrder).filter((id): id is number => typeof id === 'number');

        if (ids.length === 0) return;

        setIsBulkDeleting(true);
        try {
            await Promise.all(ids.map((id) => orderDelete(id, headers)));
            setSelectedOrderIds([]);
            await refreshOrders(filterData.client?.idClient ? filterData.client : null);
        } catch (e) {
            console.error('Bulk delete failed', e);
        } finally {
            setIsBulkDeleting(false);
        }
    }

    const requestDeleteConfirmation = (mode: 'selected' | 'allVisible') => {
        const ids =
            mode === 'selected'
                ? selectedOrderIds
                : orders.map((o) => o.idOrder).filter((id): id is number => typeof id === 'number');

        if (ids.length === 0) return;

        setPendingDeleteMode(mode);
        setConfirmDialogTitle('Konfirmo fshirjen');
        setConfirmDialogMessage(`A jeni i sigurt që doni t’i fshini ${ids.length} porosi? Ky veprim nuk mund të kthehet.`);
        setIsConfirmDialogOpen(true);
    }

    const confirmDelete = async () => {
        if (!pendingDeleteMode) return;
        setIsConfirmDialogOpen(false);
        const mode = pendingDeleteMode;
        setPendingDeleteMode(null);
        await bulkDelete(mode);
    }

    const startInlineOrderNumberEdit = (mode: 'selected' | 'allVisible') => {
        const ids =
            mode === 'selected'
                ? selectedOrderIds
                : orders.map((o) => o.idOrder).filter((id): id is number => typeof id === 'number');

        if (ids.length === 0) return;

        if (mode === 'allVisible') {
            setSelectedOrderIds(ids);
        }

        setOrderNumberDrafts((prev) => {
            const next = { ...prev };
            ids.forEach((id) => {
                if (next[id] === undefined) {
                    const order = orders.find((o) => o.idOrder === id);
                    next[id] = order?.orderNumber !== undefined && order?.orderNumber !== null ? String(order.orderNumber) : '';
                }
            });
            return next;
        });

        setIsEditingOrderNumbers(true);
    };

    const onChangeOrderNumberDraft = (idOrder: number, nextValue: string) => {
        setOrderNumberDrafts((prev) => ({ ...prev, [idOrder]: nextValue }));
    };

    const saveInlineOrderNumbers = async () => {
        if (selectedOrderIds.length === 0) return;

        const updates = selectedOrderIds
            .map((id) => {
                const raw = orderNumberDrafts[id] ?? '';
                const parsed = Number(raw);
                return { id, raw, parsed };
            })
            .filter((u) => u.raw !== '');

        if (updates.length === 0) return;

        const invalid = updates.find((u) => !Number.isInteger(u.parsed) || u.parsed <= 0);
        if (invalid) return;

        setIsBulkUpdating(true);
        try {
            await Promise.all(
                updates.map(({ id, parsed }) => {
                    const order = orders.find((o) => o.idOrder === id);
                    if (!order) return Promise.resolve(null);
                    return orderUpdate(id, { ...order, orderNumber: parsed }, headers);
                })
            );
            setIsEditingOrderNumbers(false);
            setOrderNumberDrafts({});
            setSelectedAction(null);
            await refreshOrders(filterData.client?.idClient ? filterData.client : null);
        } catch (e) {
            console.error('Inline update orderNumber failed', e);
        } finally {
            setIsBulkUpdating(false);
        }
    };

    const applySelectedAction = () => {
        if (!selectedAction) return;

        if (selectedAction === 'delete_selected') {
            requestDeleteConfirmation('selected');
            return;
        }
        if (selectedAction === 'delete_all') {
            requestDeleteConfirmation('allVisible');
            return;
        }
        if (selectedAction === 'update_orderNumber_selected') {
            if (isEditingOrderNumbers) {
                void saveInlineOrderNumbers();
                return;
            }
            startInlineOrderNumberEdit('selected');
            return;
        }
        if (selectedAction === 'update_orderNumber_all') {
            if (isEditingOrderNumbers) {
                void saveInlineOrderNumbers();
                return;
            }
            startInlineOrderNumberEdit('allVisible');
            return;
        }
    }

    const handleOnExport = () => {
        let wb = XLSX.utils.book_new();
        let ws = XLSX.utils.json_to_sheet(filterData.orders);

        XLSX.utils.book_append_sheet(wb, ws, `Raports`);
        XLSX.writeFile(wb, `Raports(${dayjs(filterData.startDate).format('DD.MM.YYYY')}-${dayjs(filterData.endDate).format('DD.MM.YYYY')}).xlsx`);
    }

    const downloadPDF = async () => {
        if (!tableRef.current) return;

        setIsGeneratingPDF(true);
        
        setTimeout(async () => {
            try {
                const element = tableRef.current;
                if (!element) {
                    setIsGeneratingPDF(false);
                    return;
                }

                // Temporarily make visible for capture
                const originalVisibility = element.style.visibility;
                const originalOpacity = element.style.opacity;
                element.style.visibility = 'visible';
                element.style.opacity = '1';
                
                await new Promise(resolve => setTimeout(resolve, 100));

                const canvas = await html2canvas(element, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.8);
                const pdf = new jsPDF('p', 'mm', 'a4');
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const imgWidth = canvas.width;
                const imgHeight = canvas.height;
                const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
                const imgX = (pdfWidth - imgWidth * ratio) / 2;
                const imgY = 5;

                pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
                
                // Restore original visibility
                element.style.visibility = originalVisibility;
                element.style.opacity = originalOpacity;
                
                pdf.save(`Raports(${dayjs(filterData.startDate).format('DD.MM.YYYY')}-${dayjs(filterData.endDate).format('DD.MM.YYYY')}).pdf`);
                setIsGeneratingPDF(false);
            } catch (error) {
                console.error('Error generating PDF:', error);
                setIsGeneratingPDF(false);
            }
        }, 300);
    };

    const downloadInvoicesPDF = async () => {
        if (!invoicesRef.current || orders.length === 0) return;

        setIsGeneratingInvoicesPDF(true);
        
        setTimeout(async () => {
            try {
                const element = invoicesRef.current;
                if (!element) {
                    setIsGeneratingInvoicesPDF(false);
                    return;
                }

                // Temporarily make visible for capture
                const originalVisibility = element.style.visibility;
                const originalOpacity = element.style.opacity;
                element.style.visibility = 'visible';
                element.style.opacity = '1';
                
                await new Promise(resolve => setTimeout(resolve, 100));

                const pdf = new jsPDF('p', 'mm', 'a4');

                // Get all invoice boxes (direct children of the ref)
                const invoiceBoxes = element.children;
                
                for (let i = 0; i < invoiceBoxes.length; i++) {
                    const invoiceBox = invoiceBoxes[i] as HTMLElement;
                    
                    if (i > 0) {
                        pdf.addPage();
                    }
                    
                    // Capture each invoice individually
                    const canvas = await html2canvas(invoiceBox, {
                        scale: 2.5,
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#ffffff',
                        allowTaint: true
                    });

                    const imgData = canvas.toDataURL('image/jpeg', 0.85);
                    
                    // Add image to fit A4 page
                    pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
                }
                
                // Restore original visibility
                element.style.visibility = originalVisibility;
                element.style.opacity = originalOpacity;
                
                pdf.save(`Fatura(${dayjs(filterData.startDate).format('DD.MM.YYYY')}-${dayjs(filterData.endDate).format('DD.MM.YYYY')}).pdf`);
                setIsGeneratingInvoicesPDF(false);
            } catch (error) {
                console.error('Error generating PDF:', error);
                setIsGeneratingInvoicesPDF(false);
            }
        }, 300);
    };

    return(
        <Layout>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 8
                }}
            >
                <Container maxWidth="xl">
                    <Stack spacing={3}>
                        <Card sx={{
                            py: 4,
                            '@media screen and (max-width: 576px)': { px: 3}
                        }}>
                            <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={2}
                                justifyContent="center"
                                sx={{'@media screen and (max-width: 576px)': { flexDirection: 'column' }}}
                            >
                                <FormControl sx={{width: '37%', '@media screen and (max-width: 576px)': { width: '100%' }}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DemoContainer components={['DatePicker']} sx={{paddingTop: '0 !important'}}>
                                            <DatePicker
                                                label="Nga"
                                                value={dayjs(filterData?.startDate)}
                                                onChange={(val) => onDateChange('startDate', val)}
                                                format={'DD/MM/YYYY'}
                                                sx={{width: '100% !important'}}
                                            />
                                        </DemoContainer>
                                    </LocalizationProvider>
                                </FormControl>

                                <FormControl sx={{width: '37%', '@media screen and (max-width: 576px)': { width: '100%' }}}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DemoContainer components={['DatePicker']} sx={{paddingTop: '0 !important'}}>
                                            <DatePicker
                                                label="Deri"
                                                value={dayjs(filterData?.endDate)}
                                                onChange={(val) => onDateChange('endDate', val)}
                                                format={'DD/MM/YYYY'}
                                                sx={{width: '100% !important'}}
                                            />
                                        </DemoContainer>
                                    </LocalizationProvider>
                                </FormControl>

                                <Button
                                    variant="contained"
                                    onClick={filterByDate}
                                    sx={{width: '15%', '@media screen and (max-width: 576px)': {width: '100%'}}}
                                >
                                    Gjenero Raportin
                                </Button>
                            </Stack>
                        </Card>

                        {orders.length > 0 &&
                            <Stack spacing={3}>
                                <Card sx={{
                                    py: 4,
                                    px: 5,
                                    '@media screen and (max-width: 576px)': {px: 3}
                                }}>
                                    <Box display="flex" justifyContent="end" mb="20px">
                                        {/*<Link to={`/reports/pdf-viewer`}>*/}
                                        <Button
                                            onClick={handleOnExport}
                                            startIcon={<DownloadIcon />}
                                            sx={{
                                                backgroundColor: '#F3F4F6',
                                                color: 'black',
                                                mr: 1,
                                                textTransform: 'none'
                                            }}
                                        >
                                            Shkarko excel
                                        </Button>
                                        <FormControl
                                            size="small"
                                            sx={{
                                                minWidth: 220,
                                                mr: 1,
                                                backgroundColor: '#F3F4F6',
                                                borderRadius: 1
                                            }}
                                        >
                                            <InputLabel id="reports-actions-label">Veprimet</InputLabel>
                                            <Select
                                                labelId="reports-actions-label"
                                                label="Veprimet"
                                                value={selectedAction ?? ''}
                                                onChange={(e) => {
                                                    const next = (e.target.value || null) as any;
                                                    setSelectedAction(next);
                                                    if (next !== 'update_orderNumber_selected' && next !== 'update_orderNumber_all') {
                                                        setIsEditingOrderNumbers(false);
                                                        setOrderNumberDrafts({});
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    Zgjidh veprimin
                                                </MenuItem>
                                                <MenuItem value="delete_selected" disabled={selectedOrderIds.length === 0}>
                                                    Fshij te selektuara ({selectedOrderIds.length})
                                                </MenuItem>
                                                <MenuItem value="delete_all" disabled={orders.length === 0}>
                                                    Fshij te gjitha ({orders.length})
                                                </MenuItem>
                                                <MenuItem value="update_orderNumber_selected" disabled={selectedOrderIds.length === 0}>
                                                    Ndrysho nr. porosise (te selektuara)
                                                </MenuItem>
                                                <MenuItem value="update_orderNumber_all" disabled={orders.length === 0}>
                                                    Ndrysho nr. porosise (te gjitha)
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button
                                            variant="contained"
                                            onClick={applySelectedAction}
                                            disabled={!selectedAction || isBulkDeleting || isBulkUpdating}
                                            sx={{ mr: 1, textTransform: 'none', minWidth: 90 }}
                                        >
                                            {(isEditingOrderNumbers && (selectedAction === 'update_orderNumber_selected' || selectedAction === 'update_orderNumber_all'))
                                                ? 'Ruaj'
                                                : 'Apliko'}
                                        </Button>
                                        <Button 
                                            onClick={downloadInvoicesPDF} 
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#1976d2',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontSize: '14px',
                                                px: 2,
                                                minWidth: '180px',
                                                '&:hover': {
                                                    backgroundColor: '#1565c0'
                                                }
                                            }} 
                                            disabled={orders.length === 0 || isGeneratingInvoicesPDF}
                                            startIcon={isGeneratingInvoicesPDF ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                                        >
                                            {isGeneratingInvoicesPDF ? 'Duke shkarkuar...' : 'Shkarko Faturat PDF'}
                                        </Button>
                                        {/*</Link>*/}
                                    </Box>
                                    <ReportsTable
                                        orders={orders}
                                        selectedOrderIds={selectedOrderIds}
                                        onToggleOrderId={onToggleOrderId}
                                        onToggleAllVisible={onToggleAllVisible}
                                        onClearSelection={clearSelectionAndAction}
                                        isEditingOrderNumbers={isEditingOrderNumbers}
                                        orderNumberDrafts={orderNumberDrafts}
                                        onChangeOrderNumberDraft={onChangeOrderNumberDraft}
                                    />
                                </Card>
                            </Stack>
                        }

                        {
                            (orders.length === 0 && filterData.isFilteredByDate) &&
                            (
                                <Stack spacing={3}>
                                    <Card sx={{
                                        py: 4,
                                        px: 5,
                                        '@media screen and (max-width: 576px)': {px: 3}
                                    }}>
                                        <Typography>Nuk është gjetur asnjë blerje</Typography>
                                    </Card>
                                </Stack>
                            )
                        }
                    </Stack>
                </Container>
                    <OrderReceiptPdf2
                        ref={tableRef}
                        orders={orders}
                        global={globalData}
                        totalPrice={totalPrice}
                        totalKg={totalKilograms}
                        isGeneratingPDF={isGeneratingPDF}
                    />
                    <OrderInvoicesPdf
                        ref={invoicesRef}
                        orders={orders}
                        global={globalData}
                        isGeneratingPDF={isGeneratingInvoicesPDF}
                    />
                    <Dialog
                        open={isConfirmDialogOpen}
                        onClose={() => !isBulkDeleting && setIsConfirmDialogOpen(false)}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>{confirmDialogTitle}</DialogTitle>
                        <DialogContent>
                            <Typography>{confirmDialogMessage}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsConfirmDialogOpen(false)} disabled={isBulkDeleting}>
                                Anulo
                            </Button>
                            <Button variant="contained" color="error" onClick={confirmDelete} disabled={isBulkDeleting}>
                                {isBulkDeleting ? 'Duke fshire...' : 'Konfirmo'}
                            </Button>
                        </DialogActions>
                    </Dialog>
            </Box>
        </Layout>
    )
}

